import { useContext, useMemo } from 'react';
import { Store } from '..//store';

/*
 * ===================================================================================
 * Store Stateから派生する値を計算するカスタムフック
 * ===================================================================================
 *
 * このファイルは、アプリケーションのグローバルなStore（Context）のStateから派生する
 * 値を計算するための、再利用可能なカスタムフックをまとめる場所です。
 *
 * 【カスタムフックとして実装する全体的なメリット】
 *
 * 1. ロジックのカプセル化（関心の分離）:
 * 複雑な計算ロジックをコンポーネントから分離し、フック内に閉じ込めます。
 * コンポーネントは計算方法の詳細を知る必要がなく、「何がしたいか」に集中できます。
 *
 * 2. 再利用性の向上:
 * 同じ計算ロジックを複数のコンポーネントで簡単に再利用でき、コードの重複を防ぎます。
 *
 * 3. 依存性の集約:
 * `useContext`によるStoreへの依存をフック内に集約できます。これにより、コンポーネントは
 * 計算のためだけにStoreのstateを意識する必要がなくなります。
 *
 * 4. パフォーマンスの最適化:
 * フック内で`useMemo`を利用することで、計算結果をメモ化（キャッシュ）できます。
 * これにより、不要な再計算を防ぎ、パフォーマンスを向上させることができます。
 * この最適化はフックの利用側からは透過的です。
 *
 * 5. 可読性と保守性の向上:
 * コンポーネントのコードが `const value = useMyValue();` のように宣言的になり、
 * 可読性が向上します。また、ロジックの修正はフックの定義ファイルを一箇所変更するだけで
 * 済むため、メンテナンスが容易になります。
 * 
 * 6. コンポーネントでの利用例:
 * 作成したカスタムフックは、以下のように呼び出すだけで、
 * 目的の値を取得できます。
 *
 * import { useIsReadOnly } from './hooks/useStoreGetters';
 *
 * const SomeComponent = () => {
 *  const isReadOnly = useIsReadOnly();
 *
 *  return (
 *   <div>{isReadOnly ? '読み取り専用モードです' : '編集できます'}</div>
 *  );
 * };
 */

/**
 * 現在のStateが読み取り専用モードかどうかを判定します。
 * Stateの`userId`、`userType`、`readUserId`を基に判定を行います。
 * @returns {boolean} 読み取り専用モードであればtrue、そうでなければfalse
 */
export const useIsReadOnly = (): boolean => {
  const { state } = useContext(Store);

  // useMemoを使い、依存するstateプロパティが変更された時のみ再計算する
  // 実用上条件2で足りているが、念のため条件1, 3, 4を追加
  const isReadOnly = useMemo(() => {
    return state.userType === 0 && // 条件1: userType === 0：教職員
      state.readUser !== null && // 条件2: readUserがnullでない
      typeof state.readUser.readUserId === 'number' && // 条件3: readUserIdに数値が格納されている
      state.userId !== state.readUser.readUserId // 条件4: ログインユーザーのID:userIdと、選択されたID:readUserIdは異なっている
  }, [state.userType, state.userId, state.readUser]);

  return isReadOnly;
};

/**
 * 有効なuserIdを取得します。
 * 読み取り専用モードのときは、state.readUser.readUserId
 * そうでない場合は、state.userId を返します。
 * @returns {number}
 */
export const useUserId = (): number => {
  const { state } = useContext(Store);
  const isReadOnly = useIsReadOnly();

  // isReadOnly, state.readUser, state.userId のいずれかが変更された場合のみ値を再計算
  return useMemo(() => {
    return isReadOnly && state.readUser ? state.readUser.readUserId : state.userId;
  }, [isReadOnly, state.readUser, state.userId]);
};

/**
 * 表示するべきニックネームを取得します。
 * 読み取り専用モードのときは、state.readUser.readUserNickname
 * そうでない場合は、state.userNickname を返します。
 * @returns {string}
 */
export const useUserNickname = (): string => {
  const { state } = useContext(Store);
  const isReadOnly = useIsReadOnly();

  // isReadOnly, state.readUser, state.userNickname のいずれかが変更された場合のみ値を再計算
  return useMemo(() => {
    return isReadOnly && state.readUser ? state.readUser.readUserNickname : state.userNickname;
  }, [isReadOnly, state.readUser, state.userNickname]);
};

/**
 * 表示するべき学籍番号を取得します。
 * 読み取り専用モードのときは、state.readUser.readUserStudentNumber
 * そうでない場合は、state.userStudentNumber を返します。
 * @returns {string}
 */
export const useUserStudentNumber = (): string => {
  const { state } = useContext(Store);
  const isReadOnly = useIsReadOnly();

  // isReadOnly, state.readUser, state.userStudentNumber のいずれかが変更された場合のみ値を再計算
  return useMemo(() => {
    return isReadOnly && state.readUser ? state.readUser.readUserStudentNumber : state.userStudentNumber;
  }, [isReadOnly, state.readUser, state.userStudentNumber]);
};

/**
 * state.languageを取得します。
 * @returns {string}
 */
export const useLanguage = (): string => {
  const { state } = useContext(Store);
  return useMemo(() => {
    return state.language;
  }, [state.language]);
};
